import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ServerErrorPage } from '@/pages/ServerErrorPage';

/**
 * The one class component in the app.
 *
 * Not a style choice: `componentDidCatch` and `getDerivedStateFromError` have no hook equivalent,
 * and without them React 18 answers an uncaught render error by unmounting the entire tree — a
 * white page with an explanation only in the console, where no shop owner will ever look.
 *
 * It catches renders, lifecycle and constructors. It does **not** catch event handlers, async
 * callbacks or anything thrown outside the render pass, which is most of what actually goes wrong
 * in this app — those already surface as `ApiError` through `apiFetch` and are reported where they
 * happened. This is the floor under the cases nothing else can see.
 */
interface State {
  /** The error that unmounted the tree, or null while everything is fine. */
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Until there is somewhere to send these, the console is the record. Deliberately kept even
    // though React logs its own copy: this one carries the component stack, which is the half that
    // says *where*.
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return <ServerErrorPage detail={this.state.error.message} />;
    }

    return this.props.children;
  }
}
